import { CommonModule } from '@angular/common';
import {
  Component,
  input,
  output,
  signal,
  computed,
  effect,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';

import { TranslateLangPipe } from '../../pipes/translate-lang.pipe';

/**
 * Represents a single checkbox item in the criteria selector
 */
export interface CriteriaItem {
  /**
   * Unique identifier for the item
   */
  id: string;
  /**
   * Display label for the item (can be a translation key)
   */
  label: string;
  /**
   * Whether this item is selected
   */
  selected: boolean;
}

/**
 * A criteria selector component that displays a title, icon, and list of checkbox items.
 * Designed based on Figma design for filtering/sorting functionality.
 * Figma URL: https://www.figma.com/design/W3VBXvQM2bo98mSNlDSzhi/GivingSpace-Reimagining?node-id=1-4076
 *
 * @example
 * ```html
 * <app-criteria-selector
 *   [icon]="'solar:round-sort-vertical-outline'"
 *   [title]="'Sort By'"
 *   [items]="sortItems"
 *   (selectionChanged)="onSortChanged($event)">
 * </app-criteria-selector>
 * ```
 */
@Component({
  selector: 'app-criteria-selector',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, TranslateLangPipe],
  templateUrl: './criteria-selector.component.html',
  styleUrls: ['./criteria-selector.component.scss'],
})
export class CriteriaSelectorComponent {
  /**
   * Icon to display (can be Material icon name or SVG URL)
   */
  icon = input<string>('');

  /**
   * Title text to display (for default variant)
   */
  title = input<string>('');

  /**
   * Dropdown title text (for dropdown variant)
   */
  dropdownTitle = input<string>('');

  /**
   * Whether to show the main title (for dropdown variant)
   */
  showMainTitle = input<boolean>(true);

  /**
   * Array of checkbox items
   */
  items = input<CriteriaItem[]>([]);

  /**
   * Whether to allow multiple selections (default: true)
   */
  multiSelect = input<boolean>(true);

  /**
   * Variant of the component ('default' | 'dropdown')
   */
  variant = input<'default' | 'dropdown'>('default');

  /**
   * Initial expanded state for dropdown variant (default: true)
   */
  initialExpanded = input<boolean>(true);

  /**
   * Emits the updated items when selection changes
   */
  selectionChanged = output<CriteriaItem[]>();

  /**
   * Internal signal to track items
   */
  internalItems = signal<CriteriaItem[]>([]);

  /**
   * Whether the dropdown is expanded (only for dropdown variant)
   */
  isExpanded = signal<boolean>(true);

  /**
   * Search query for filtering items (only for dropdown variant)
   */
  searchQuery = signal<string>('');

  /**
   * Computed property to get selected items
   */
  selectedItems = computed(() => {
    return this.internalItems().filter((item) => item.selected);
  });

  /**
   * Computed property to get filtered items based on search query
   */
  filteredItems = computed(() => {
    const query = this.searchQuery().toLowerCase();
    if (!query) {
      return this.internalItems();
    }
    return this.internalItems().filter((item) =>
      item.label.toLowerCase().includes(query)
    );
  });

  constructor() {
    // Sync internal items with input
    this.internalItems.set(this.items());

    // Watch for changes in items input and sync to internal items
    effect(() => {
      const items = this.items();
      this.internalItems.set([...items]);
    });
  }

  ngOnInit() {
    // Sync internal items with input
    this.internalItems.set(this.items());
    // Set initial expanded state
    this.isExpanded.set(this.initialExpanded());
  }

  /**
   * Handles checkbox toggle
   */
  toggleItem(item: CriteriaItem): void {
    const items = this.internalItems();
    const updatedItems = items.map((i) => {
      if (i.id === item.id) {
        return { ...i, selected: !i.selected };
      }
      // If single select mode, deselect other items
      if (!this.multiSelect() && i.id !== item.id) {
        return { ...i, selected: false };
      }
      return i;
    });

    this.internalItems.set(updatedItems);
    this.selectionChanged.emit(updatedItems);
  }

  /**
   * Checks if the icon is a URL (for custom SVG/image)
   */
  isIconUrl(icon: string): boolean {
    return icon.startsWith('http') || icon.startsWith('/') || icon.startsWith('.');
  }

  /**
   * Toggles the dropdown expansion state
   */
  toggleDropdown(): void {
    this.isExpanded.set(!this.isExpanded());
  }

  /**
   * Updates the search query
   */
  onSearchChange(query: string): void {
    this.searchQuery.set(query);
  }
}
